import { useEffect } from "react";
import { X, TrendingUp, DollarSign, Users, Activity } from "lucide-react";
import type { Package } from "../../types/package";

interface PackageStats {
  package: {
    id: string;
    title: string;
    price: number;
    originalPrice?: number;
    discountPercentage?: number;
  };
  enrollments: {
    total: number;
    byStatus: { _id: string; count: number; totalRevenue: number }[];
  };
  revenue: {
    total: number;
    currency: string;
  };
}

interface PackageStatsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pkg?: Package | null;
  stats?: PackageStats | null;
  isLoading?: boolean;
}

export function PackageStatsDrawer({
  isOpen,
  onClose,
  pkg,
  stats,
  isLoading,
}: PackageStatsDrawerProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !pkg) return null;

  const statusCounts =
    stats?.enrollments.byStatus?.reduce<Record<string, number>>((acc, item) => {
      acc[item._id] = item.count;
      return acc;
    }, {}) || {};

  return (
    <div className="fixed inset-0 z-40 flex">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative ml-auto flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">
              Package stats
            </p>
            <h2 className="text-xl font-bold text-gray-900">{pkg.title}</h2>
          </div>
          <button
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isLoading && (
            <div className="flex h-40 items-center justify-center text-gray-500">
              Loading stats...
            </div>
          )}

          {!isLoading && stats && (
            <div className="space-y-6">
              <section className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between text-gray-500">
                    <span className="text-xs font-semibold uppercase">
                      Total revenue
                    </span>
                    <DollarSign size={18} className="text-emerald-500" />
                  </div>
                  <p className="mt-3 text-2xl font-semibold text-gray-900">
                    {stats.revenue.total.toLocaleString()}{" "}
                    <span className="text-sm text-gray-500">
                      {stats.revenue.currency}
                    </span>
                  </p>
                </div>

                <div className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between text-gray-500">
                    <span className="text-xs font-semibold uppercase">
                      Total purchases
                    </span>
                    <TrendingUp size={18} className="text-indigo-500" />
                  </div>
                  <p className="mt-3 text-2xl font-semibold text-gray-900">
                    {stats.enrollments.total}
                  </p>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-100 p-4">
                <p className="text-xs font-semibold uppercase text-gray-400">
                  Enrollment status
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl bg-emerald-50 p-4">
                    <div className="flex items-center gap-3">
                      <Users size={20} className="text-emerald-600" />
                      <div>
                        <p className="text-xs uppercase text-emerald-600">
                          Active
                        </p>
                        <p className="text-2xl font-semibold text-emerald-800">
                          {statusCounts.active || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <div className="flex items-center gap-3">
                      <Activity size={20} className="text-slate-600" />
                      <div>
                        <p className="text-xs uppercase text-slate-500">
                          Completed
                        </p>
                        <p className="text-2xl font-semibold text-slate-800">
                          {statusCounts.completed || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
